import type { PageMeta, Paged } from '../types/portal'

/**
 * Shared plumbing for the portal API: the error shape, the /api/v1 envelope, and
 * pagination. Matches the specification's error format exactly, so the interface
 * handles a real NestJS error and a preview error the same way.
 */

export const PORTAL_API_BASE = '/api/v1'

export type PortalErrorCode =
  | 'VALIDATION_ERROR'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'RATE_LIMITED'
  | 'INTERNAL_ERROR'

const STATUS_FOR: Record<PortalErrorCode, number> = {
  VALIDATION_ERROR: 422,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  RATE_LIMITED: 429,
  INTERNAL_ERROR: 500,
}

const DEFAULT_MESSAGE: Record<PortalErrorCode, string> = {
  VALIDATION_ERROR: 'Some details need correcting.',
  UNAUTHORIZED: 'Your session has ended. Please sign in again.',
  FORBIDDEN: 'You do not have permission to access this resource.',
  NOT_FOUND: 'We could not find that. It may have been moved, or you may not have access to it.',
  RATE_LIMITED: 'Too many requests. Please wait a few minutes and try again.',
  INTERNAL_ERROR: 'Something went wrong on our side. Please try again.',
}

export class PortalApiError extends Error {
  code: PortalErrorCode
  status: number
  details: Record<string, string>

  constructor(code: PortalErrorCode, message?: string, details: Record<string, string> = {}) {
    super(message ?? DEFAULT_MESSAGE[code])
    this.code = code
    this.status = STATUS_FOR[code]
    this.details = details
  }
}

export interface PageQuery {
  page?: number
  limit?: number
}

export function paginate<T>(rows: T[], { page = 1, limit = 25 }: PageQuery = {}): Paged<T> {
  const safeLimit = Math.min(Math.max(limit, 1), 100)
  const totalPages = Math.max(1, Math.ceil(rows.length / safeLimit))
  const safePage = Math.min(Math.max(page, 1), totalPages)
  const meta: PageMeta = { page: safePage, limit: safeLimit, total: rows.length, totalPages }
  return { data: rows.slice((safePage - 1) * safeLimit, safePage * safeLimit), meta }
}

/** Live transport. Same origin, cookies always sent, envelope unwrapped. */
export async function http<T>(method: 'GET' | 'POST' | 'PATCH' | 'DELETE', path: string, body?: unknown): Promise<T> {
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), 15_000)
  try {
    const response = await fetch(`${PORTAL_API_BASE}${path}`, {
      method,
      credentials: 'include',
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
    })
    if (response.status === 204) return undefined as T
    const json = (await response.json().catch(() => null)) as
      | { data?: unknown; meta?: PageMeta; error?: { code: PortalErrorCode; message: string; details?: Record<string, string> } }
      | null
    if (!response.ok || !json || json.error) {
      const error = json?.error
      throw new PortalApiError(error?.code ?? 'INTERNAL_ERROR', error?.message, error?.details)
    }
    return (json.meta ? { data: json.data, meta: json.meta } : json.data) as T
  } catch (error) {
    if (error instanceof PortalApiError) throw error
    throw new PortalApiError('INTERNAL_ERROR', 'We could not reach the portal. Check your connection and try again.')
  } finally {
    window.clearTimeout(timer)
  }
}

export function queryString(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, value]) => value !== undefined && value !== '')
  return entries.length ? `?${new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString()}` : ''
}

/** Simulated network latency for the preview, so loading states are exercised. */
export function latency(ms = 220): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms))
}

export function newId(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`
}
